import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  PractitionerGetAllResponse,
  PractitionerSearch,
} from '@quromedical/models';
import { CrudForm } from 'components/form';
import { WithGroup } from 'core/auth';
import {
  columns,
  initialSearch,
  type PractitionerTable,
  searchFields,
} from 'core/practitioner';
import { PaginatedTable } from 'core/table';
import { Section, ToggleCard } from 'design-system';
import { router } from 'expo-router';
import type { FetcherOptions } from 'hooks/usePaginatedData';
import { useResponsive } from 'hooks/useResponsive';
import type { PractitionerStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';
import { trpcClient } from 'trpc';

import { PractitionerCreateForm } from './card';

const getDataFromResponse = (
  res: PractitionerGetAllResponse,
): PractitionerTable[] =>
  res.practitioners.map((p) => ({
    id: p.id,
    givenName: p.general.givenName,
    familyName: p.general.familyName,
    email: p.contact.telecomEmail,
    phone: p.contact.telecomPhoneNumbers?.[0],
    secondaryPhone: p.contact.telecomPhoneNumbers?.[1],
    registrationNumber: p.qualification.registrationNumber,
  }));

export const PractitionerListScreen: React.FC<
  PractitionerStackScreenProps<'List'>
> = () => {
  const isStriped = useResponsive({ md: true }, false);

  const [search, setSearch] = useState<PractitionerSearch>(initialSearch);

  const fetcher = useCallback(
    (options?: FetcherOptions) =>
      trpcClient.practitioner.getAll.query({
        name: search.name || undefined,
        email: search.email || undefined,
        sort: search.sort,
        pageToken: options?.nextToken,
        ...options,
      }),
    [search],
  );

  const onRowPress = useCallback((row: PractitionerTable) => {
    const id = row.id;
    if (!id) return;

    router.push(`/admin/practitioner/${id}`);
  }, []);

  return (
    <ScrollView>
      <WithGroup groups={[GroupSecurity.PractitionerCreate]}>
        <Section unsetZIndex>
          <ToggleCard
            icon="person-outline"
            title={strings.PractitionerToggleCardTitle}
            textExpand={strings.PractitionerToggleCardExpandButtonText}
            textCollapse={strings.PractitionerToggleCardCollapseButtonText}
          >
            <PractitionerCreateForm />
          </ToggleCard>
        </Section>
      </WithGroup>
      <Section unsetZIndex>
        <CrudForm
          cardProps={{ unsetZIndex: true }}
          fields={searchFields}
          initialValues={initialSearch}
          showSubmitButton={false}
          onChange={setSearch}
        />
      </Section>
      <Section>
        <PaginatedTable
          cacheKey={`Practitioner${JSON.stringify(search)}`}
          fetcher={fetcher}
          columns={columns}
          showHeader={true}
          isStriped={isStriped}
          getDataFromResponse={getDataFromResponse}
          onRowPress={onRowPress}
        />
      </Section>
    </ScrollView>
  );
};
